/* ──────────────────────────────────────────────────────────────
   Marè — script.js
   No cart. Colour + size → Shopify cart permalink → checkout.
   Everything swappable lives in CONFIG.
   ────────────────────────────────────────────────────────────── */

const CONFIG = {
  SHOP_DOMAIN: "jxbpy6-sy.myshopify.com",

  // Shopify variant IDs, keyed colour → size.
  VARIANTS: {
    "Wine Red": { S: 53266248466613, M: 53266248499381, L: 53266248532149,
                  XL: 53266248564917, "2XL": 53266248597685, "3XL": 53266248630453 },
    "Black":    { S: 53266248663221, M: 53266248695989, L: 53266248728757,
                  XL: 53266248761525, "2XL": 53266248794293, "3XL": 53266248827061 }
  },

  // Per-colour plates. `slug` is what a shareable URL hash carries (#wine-red).
  COLORS: {
    "Black": {
      slug: "black",
      front: "images/sun-raglan-black-front.webp",
      back:  "images/sun-raglan-black-back.webp"
    },
    "Wine Red": {
      slug: "wine-red",
      front: "images/sun-raglan-wine-red-front.webp",
      back:  "images/sun-raglan-wine-red-back.webp"
    }
  },

  SIZES: ["S", "M", "L", "XL", "2XL", "3XL"],
  PRODUCT_NAME: "Sun Raglan",
  PRICE: "$34.99",

  // Shopify's standard policy pages, relative to SHOP_DOMAIN.
  POLICIES: {
    shipping: "/policies/shipping-policy",
    returns:  "/policies/refund-policy",
    terms:    "/policies/terms-of-service"
  }
};

/* ─── Pure helpers (exported for tests) ─────────────────────── */

/** Shopify cart permalink: https://{shop}/cart/{variant}:1 → straight to checkout. */
function checkoutUrl(color, size, config = CONFIG) {
  const byColor = config.VARIANTS[color];
  const id = byColor && byColor[size];
  if (!id) return null;
  const base = config.CART_URL || `https://${config.SHOP_DOMAIN}/cart/`;   // CART_URL is set by the Shopify theme
  return `${base}${id}:1`;
}

function policyUrl(key, config = CONFIG) {
  const path = config.POLICIES[key];
  return path ? `https://${config.SHOP_DOMAIN}${path}` : null;
}

/** "#wine-red" (or "wine-red") → "Wine Red"; anything else → null. */
function colorFromSlug(slug, config = CONFIG) {
  const s = String(slug || "").replace(/^#/, "").toLowerCase();
  for (const [name, c] of Object.entries(config.COLORS)) {
    if (c.slug === s) return name;
  }
  return null;
}

function slugOf(color, config = CONFIG) {
  const c = config.COLORS[color];
  return c ? c.slug : null;
}

/** Centimetres → inches, one decimal, no trailing ".0". */
function cmToIn(cm) {
  const n = Number(cm);
  if (!Number.isFinite(n)) return "";
  return String(Math.round((n / 2.54) * 10) / 10);
}

function plateCaption(color, view, config = CONFIG) {
  return `${config.PRODUCT_NAME} — ${color}, ${view}`;
}

const PLATE_ALT = {
  front: (name, color) => `${name} in ${color}, front. ${color} raglan tee with white piping along the shoulder seams and side panels, the Marè sun mark printed at the chest.`,
  back:  (name, color) => `${name} in ${color}, back. Plain back with the white piping continuing over the raglan seams.`
};
function plateAlt(color, view, config = CONFIG) {
  const f = PLATE_ALT[view];
  return f ? f(config.PRODUCT_NAME, color) : `${config.PRODUCT_NAME} in ${color}, ${view}.`;
}

/* ─── DOM ───────────────────────────────────────────────────── */

/** The Shopify theme emits <script type="application/json" id="mare-config"> from Liquid; merge it over CONFIG. */
function applyInjectedConfig(doc, config = CONFIG) {
  const tag = doc.getElementById("mare-config");
  if (!tag) return config;
  try { Object.assign(config, JSON.parse(tag.textContent)); } catch (e) { /* leave the defaults */ }
  return config;
}

function init(doc = document, win = window) {
  const $  = (s, root = doc) => root.querySelector(s);
  const $$ = (s, root = doc) => Array.from(root.querySelectorAll(s));
  applyInjectedConfig(doc);

  const reduceMotion = () =>
    win.matchMedia && win.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const els = {
    form:        $("#buy-form"),
    buy:         $("#buy"),
    colorInputs: $$('input[name="color"]'),
    sizeInputs:  $$('input[name="size"]'),
    colorValue:  $("#color-value"),
    sizeValue:   $("#size-value"),
    sizeOpt:     $("#opt-size"),
    plates:      $$(".plate[data-plate]"),
    unitBtns:    $$(".units__btn"),
    cmCells:     $$("[data-cm]"),
    navLinks:    $$(".masthead__nav a"),
    policyLinks: $$("[data-policy]"),
    shopLinks:   $$("[data-shop-link]"),
    prices:      $$("[data-price]"),
    year:        $("[data-year]")
  };

  const state = {
    color: (els.colorInputs.find(i => i.checked) || {}).value || Object.keys(CONFIG.COLORS)[0],
    size: null,
    needsSize: false
  };

  /* Plates — crossfade unless the visitor prefers reduced motion. */
  function swapImage(img, src, alt) {
    if (img.getAttribute("src") === src) { img.alt = alt; return; }
    const apply = () => {
      img.src = src;
      img.alt = alt;
      img.classList.remove("is-swapping");
    };
    const pre = new win.Image();
    pre.src = src;
    const decoded = pre.decode ? pre.decode().catch(() => {}) : Promise.resolve();
    if (reduceMotion()) { decoded.then(apply); return; }
    img.classList.add("is-swapping");
    const faded = new Promise(r => win.setTimeout(r, 180));
    Promise.all([decoded, faded]).then(apply);
  }

  function renderPlates() {
    const c = CONFIG.COLORS[state.color];
    if (!c) return;
    for (const fig of els.plates) {
      const view = fig.dataset.plate;
      const img = $("img", fig);
      const cap = $("[data-caption]", fig);
      const src = c[view];
      fig.hidden = !src;                                   // a colourway without a back render simply has no Plate II
      if (img && src) swapImage(img, src, plateAlt(state.color, view));
      if (cap) cap.textContent = plateCaption(state.color, view);
    }
  }

  function renderBuy() {
    const available = CONFIG.VARIANTS[state.color] || {};
    els.sizeInputs.forEach(i => {
      const ok = Boolean(available[i.value]);
      i.disabled = !ok;
      if (!ok && i.checked) { i.checked = false; if (state.size === i.value) state.size = null; }
    });
    if (els.colorValue) els.colorValue.textContent = state.color;
    if (els.sizeValue)  els.sizeValue.textContent  = state.size || "—";
    if (els.sizeOpt)    els.sizeOpt.classList.toggle("is-needed", state.needsSize && !state.size);

    const url = state.size ? checkoutUrl(state.color, state.size) : null;
    if (!els.buy) return;
    if (url) {
      els.buy.href = url;
      els.buy.textContent = `Buy — ${CONFIG.PRICE}`;
      els.buy.classList.remove("is-pending");
      els.buy.removeAttribute("rel");
    } else {
      els.buy.href = "#opt-size";
      els.buy.textContent = "Choose a size";
      els.buy.classList.add("is-pending");
    }
  }

  function render() { renderPlates(); renderBuy(); }

  function setColor(color, { fromHash = false } = {}) {
    if (!CONFIG.COLORS[color]) return;
    state.color = color;
    const input = els.colorInputs.find(i => i.value === color);
    if (input && !input.checked) input.checked = true;
    if (!fromHash && win.history && win.history.replaceState) {
      win.history.replaceState(null, "", `#${slugOf(color)}`);
    }
    render();
  }

  /* Colour + size selection */
  els.colorInputs.forEach(i => i.addEventListener("change", () => i.checked && setColor(i.value)));
  els.sizeInputs.forEach(i => i.addEventListener("change", () => {
    if (!i.checked) return;
    state.size = i.value;
    state.needsSize = false;
    renderBuy();
  }));

  /* Buy — a real link to Shopify once a size is chosen; otherwise point at the sizes. */
  if (els.buy) {
    els.buy.addEventListener("click", (e) => {
      if (state.size) return;                       // let the link go to checkout
      e.preventDefault();
      state.needsSize = true;
      renderBuy();
      const first = els.sizeInputs[0];
      if (els.sizeOpt) els.sizeOpt.scrollIntoView({ block: "center", behavior: reduceMotion() ? "auto" : "smooth" });
      if (first) first.focus({ preventScroll: true });
    });
  }
  if (els.form) {
    els.form.addEventListener("submit", (e) => {
      e.preventDefault();
      if (els.buy) els.buy.click();
    });
  }

  /* Deep link: #black / #wine-red selects a colourway. Section hashes are left alone. */
  function applyHash() {
    const c = colorFromSlug(win.location.hash);
    if (c) setColor(c, { fromHash: true });
  }
  win.addEventListener("hashchange", applyHash);

  /* Size chart units */
  function setUnit(unit) {
    els.unitBtns.forEach(b => b.setAttribute("aria-pressed", String(b.dataset.unit === unit)));
    els.cmCells.forEach(td => {
      td.textContent = unit === "in" ? cmToIn(td.dataset.cm) : td.dataset.cm;
    });
  }
  els.unitBtns.forEach(b => b.addEventListener("click", () => setUnit(b.dataset.unit)));

  /* Masthead index — mark the section in view */
  if ("IntersectionObserver" in win && els.navLinks.length) {
    const byId = new Map(els.navLinks.map(a => [new win.URL(a.href, win.location.href).hash.slice(1), a]));
    const sections = Array.from(byId.keys()).map(id => doc.getElementById(id)).filter(Boolean);
    const io = new win.IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const link = byId.get(entry.target.id);
        if (!link) return;
        if (entry.isIntersecting) {
          els.navLinks.forEach(a => a.removeAttribute("aria-current"));
          link.setAttribute("aria-current", "true");
        } else if (link.getAttribute("aria-current")) {
          link.removeAttribute("aria-current");
        }
      });
    }, { rootMargin: "-45% 0px -50% 0px", threshold: 0 });
    sections.forEach(s => io.observe(s));
  }

  /* Missing plates fail loudly (dashed frame + the path we looked for) instead of a broken icon. */
  els.plates.forEach(fig => {
    const img = $("img", fig);
    const frame = $(".plate__frame", fig);
    if (!img || !frame) return;
    img.addEventListener("error", () => {
      frame.dataset.missing = img.getAttribute("src");
      frame.classList.add("is-missing");
    });
    img.addEventListener("load", () => frame.classList.remove("is-missing"));
  });

  /* Store links and prices come from CONFIG so one edit updates the page. */
  els.policyLinks.forEach(a => { const u = policyUrl(a.dataset.policy); if (u) a.href = u; });
  els.shopLinks.forEach(a => { a.href = `https://${CONFIG.SHOP_DOMAIN}`; a.textContent = CONFIG.SHOP_DOMAIN; });
  els.prices.forEach(el => { el.textContent = CONFIG.PRICE; });
  if (els.year) els.year.textContent = String(new Date().getFullYear());

  /* Warm the other colourway's plates so the swap is instant. */
  const warm = () => {
    Object.entries(CONFIG.COLORS).forEach(([name, c]) => {
      if (name === state.color) return;
      [c.front, c.back].forEach(src => { const i = new win.Image(); i.src = src; });
    });
  };
  if (win.requestIdleCallback) win.requestIdleCallback(warm, { timeout: 2000 }); else win.setTimeout(warm, 800);

  applyHash();
  render();
  setUnit("cm");

  return { state, setColor, setUnit, render };
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init());
  else init();
}

/* Node (tests) */
if (typeof module !== "undefined" && module.exports) {
  module.exports = { CONFIG, checkoutUrl, policyUrl, colorFromSlug, slugOf, cmToIn, plateCaption, plateAlt, applyInjectedConfig, init };
}
